import React from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { MapPin } from 'lucide-react'

function formatActivityDate(dateStr) {
	if (!dateStr) return ''
	const d = new Date(dateStr)
	const now = new Date()
	const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const sevenDaysAgo = new Date(todayStart)
	sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

	if (d >= todayStart) {
		return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })
	}
	if (d >= sevenDaysAgo) {
		return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
	}
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function SidebarLocationsRecent() {
	const { recents, isLoading } = useSelector(state => {
		const recentIds = state.locations.recents
		const recentLocations = recentIds.map(rId => state.locations.entities[rId] || {})
		return {
			recents: recentLocations || [],
			isLoading: state.locations.isLoading,
		}
	})

	const isEmpty = recents?.length === 0

	return (
		<div className="flex flex-col h-full">
			<div className="px-4 py-3 border-b border-gray-100">
				<div className="flex items-center gap-2">
					<svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
					</svg>
					<span className="text-sm font-semibold text-gray-900">Recent Activity</span>
					{recents.length > 0 && (
						<span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
							{recents.length}
						</span>
					)}
				</div>
			</div>
			<div className="flex-1 overflow-y-auto">
				{isLoading ? (
					<div className="flex items-center justify-center h-full p-4">
						<span className="text-sm text-gray-400">Loading...</span>
					</div>
				) : isEmpty ? (
					<div className="text-center py-6">
						<svg className="w-5 h-5 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
						</svg>
						<p className="text-sm text-gray-400">No activity yet</p>
					</div>
				) : (
					<div className="flow-root px-4 pt-3">
						<ul className="-mb-6">
							{recents.map((location, idx) => {
								const isLast = idx === recents.length - 1
								const locationName = location.retailer || location.assetName
								const locationDetail = `${location.city}, ${location.stateProvince}`
								return (
									<li key={location._id || idx} className="relative pb-6 last:pb-0">
										{!isLast && (
											<span
												className="absolute left-[15px] top-8 -ml-px h-full w-0.5 bg-gray-200"
												aria-hidden="true"
											/>
										)}
										<Link
											to={`/locations/${location._id}`}
											className="relative flex items-start gap-3 no-underline group"
										>
											<Avatar className="h-8 w-8 flex-shrink-0">
												{location.image ? (
													<>
														<AvatarImage src={location.image} alt={locationName} />
														<AvatarFallback className="text-xs bg-gray-200 text-gray-600">
															{(locationName || '?').charAt(0).toUpperCase()}
														</AvatarFallback>
													</>
												) : (
													<AvatarFallback className="bg-blue-50 text-blue-600">
														<MapPin className="w-3.5 h-3.5" />
													</AvatarFallback>
												)}
											</Avatar>
											<div className="min-w-0 flex-1 pt-0.5">
												<span className="text-sm font-medium text-gray-900 group-hover:text-[#0080fe] transition-colors">
													{locationName}
												</span>
												<p className="mt-0.5 text-xs text-gray-400">{locationDetail}</p>
												{location.updateNote && (
													<p className="mt-0.5 text-sm text-gray-500">{location.updateNote}</p>
												)}
												{location.updatedAt && (
													<p className="mt-0.5 text-xs text-gray-400">{formatActivityDate(location.updatedAt)}</p>
												)}
											</div>
										</Link>
									</li>
								)
							})}
						</ul>
					</div>
				)}
			</div>
		</div>
	)
}

export default SidebarLocationsRecent
